import { ChangeEmitter } from '@/application/ChangeEmitter';
import { ImageField, type TImageFieldParameters } from '@/domain/image/ImageField';
import { TextboxField, type TTextboxFieldParameters } from '@/domain/textbox/TextboxField';
import { computeIsEditable } from '@/application/selection/computeIsEditable';
import type { I2DEngine } from '@/application/ports/I2DEngine';
import type { IEntityRepository } from '@/application/ports/IEntityRepository';
import type { ISelectionState } from '@/application/ports/ISelectionState';
import type { TAddViewOptions } from '@/application/ports/TAddViewOptions';
import type { TUserContext } from '@/application/selection/TUserContext';
import type { AField } from '@/domain/field/AField';
import type { TPosition2D } from '@/domain/shared/TPosition2D';
import type { TSide } from '@/domain/shared/TSide';

class EntityController {
    constructor(
        private readonly _engine: I2DEngine,
        private readonly _repository: IEntityRepository,
        private readonly _selection: ISelectionState,
        private readonly _changeEmitter: ChangeEmitter,
        private readonly _side: TSide,
        private readonly _enforceOverlayZOrder: () => void,
    ) {}

    async addImage(params: TImageFieldParameters = {}): Promise<ImageField> {
        const entity = new ImageField({ ...params, side: this._side });
        await this.addEntity(entity);

        return entity;
    }

    async addTextbox(params: TTextboxFieldParameters = {}): Promise<TextboxField> {
        const entity = new TextboxField({ ...params, side: this._side });
        await this.addEntity(entity);

        return entity;
    }

    async addEntity(entity: AField, options?: TAddViewOptions): Promise<void> {
        this._repository.add(entity);
        // Await the async fabric add before re-enforcing overlay z-order:
        // otherwise images (blob load is async) land on top AFTER the
        // cutting-path mask was raised, so they escape the clip.
        await this._engine.addView(entity, options);
        this._enforceOverlayZOrder();
        this._changeEmitter.emit();
    }

    refreshTextboxesAfterHydration(entities: AField[]): void {
        this._engine.refreshTextboxesAfterHydration(entities);
    }

    async syncFromState(userCtx: TUserContext): Promise<void> {
        const fields = this._repository.getAll().filter((field) => field.side === this._side && field.hasContent());
        await Promise.all(fields.map((field) => this._engine.addView(field, { editable: computeIsEditable(field, userCtx) })));
        this._engine.refreshTextboxesAfterHydration(fields);
        this._enforceOverlayZOrder();
        this._changeEmitter.emit();
    }

    move(entityId: string, position: TPosition2D): void {
        const entity = this._repository.get(entityId);
        if (!entity) {
            return;
        }
        entity.moveTo(position);
        this._repository.update(entity);
        this._engine.updateView(entity);
        this._changeEmitter.emit();
    }

    remove(entityId: string): void {
        this._repository.remove(entityId);
        this._engine.removeView(entityId);
        this._enforceOverlayZOrder();
        this._changeEmitter.emit();
    }

    setSelected(entityId: string | undefined): void {
        this._selection.setSelectedId(entityId);
        this._engine.setSelectedEntity(entityId);
        this._changeEmitter.emit();
    }

    updateEntity(entity: AField): void {
        this._repository.update(entity);
        this._engine.updateView(entity);
        this._changeEmitter.emit();
    }
}

export { EntityController };
